/* ══════════════════════════════════════════════════════════════════════
   TeakTouch — V4 fusion data layer  ·  single source of truth
   ──────────────────────────────────────────────────────────────────────
   Replaces the prototype's four overlapping files
   (woods.js + opt-data.js + app.js builders + opt-app.js builders).

   Two prototype defects are fixed here by construction:
     · TT.designs used to exist twice — an array in woods.js and an object
       keyed by wood in opt-data.js — so app.js and opt-app.js disagreed
       about its shape. There is now exactly ONE shape: a map by wood id.
     · accessories used to carry two different id sets (TT.accessories[].id
       vs TT.parts keyed ids). There is now exactly ONE list: TT.parts, and
       TT.partOrder re-exports its order.

   No price lives in this file. Price and purchasability come only from
   window.TT_SHOP (snippets/v4-catalog.liquid → real Shopify variants).

   Unfounded claims removed from the prototype copy, layout untouched:
     · static "want" vote counts on tile-face designs
     · the permanent 5% creator royalty claim
     · "148+ clubs on the map"
     · "Ships in 3-5 days, from US stock"
     · a hard "144 tiles" tier line that contradicts the real US edition
   ══════════════════════════════════════════════════════════════════════ */

window.TT = window.TT || {};

(function (TT) {
  'use strict';

  /* ── the four woods ─────────────────────────────────────────────────── */
  TT.collections = {
    goldenhour: {
      id: 'goldenhour',
      name: 'Golden Hour',
      species: 'Teak',
      status: 'Available now',
      statusCls: 'green',
      available: true,
      light: 'Late afternoon light',
      scene: 'Scene · the full set on a lit table, late afternoon · 4:5',
      tone: 'Warm gold, late in the day. Our original grain — the one every other wood is measured against.',
      story: 'Golden Hour is where TeakTouch began. A deep, warm grain with the amber cast of late-afternoon light on old furniture. It is the heaviest-reading of our woods in the hand, and the one that hides a room\u2019s wear best.',
      palette: ['#A9764A', '#7A4B2A', '#4B2C15', '#2E1D0C', '#EADCC9'],
      dir: 'goldenhour'
    },
    sundial: {
      id: 'sundial',
      name: 'Sundial',
      species: 'Bamboo',
      status: 'Next release',
      statusCls: 'brass',
      available: false,
      light: 'Hard midday sun',
      scene: 'Scene · sun straight across the tiles, hard shadows · 4:5',
      tone: 'Sun-dried yellow, dry and bright. A lighter table for a lighter room.',
      story: 'Sundial takes its colour from bamboo that has spent a season in the light — that dry, bleached, honeyed yellow you only get from time and sun. It reads cooler than Golden Hour and brighter across a table, and it is the collection we are matching most carefully, because aged bamboo is notoriously uneven.',
      palette: ['#D2BC85', '#AE8E48', '#6A5220', '#3F3210', '#F0E7CE'],
      dir: 'sundial'
    },
    midnight: {
      id: 'midnight',
      name: 'Midnight',
      species: 'Ebony',
      status: 'In development',
      statusCls: '',
      available: false,
      light: 'Night',
      scene: 'Scene · the set at night, one warm lamp, deep shadow · 4:5',
      tone: 'Near-black and formal. The quietest grain we are working on.',
      story: 'Midnight is an ebony family — almost black, with the grain only visible when light catches it at an angle. It is the most formal set we have attempted, and the hardest to finish consistently: a grain this dark shows every imperfection in the surface.',
      palette: ['#6B6355', '#3D372D', '#241F19', '#0E0C09', '#DCD6CA'],
      dir: 'midnight'
    },
    firstlight: {
      id: 'firstlight',
      name: 'First Light',
      species: 'Bamboo & bone',
      status: 'Concept',
      statusCls: '',
      available: false,
      light: 'First light',
      scene: 'Scene · the set at dawn, pale window light, soft edges · 4:5',
      tone: 'Two tones in one tile — pale bone over bamboo, the way the old sets were made.',
      story: 'First Light is a deliberate nod to the classic bamboo-and-bone mahjong sets, where a pale face sat on a darker body. We are working out how to do that joint in a single engineered tile rather than two glued pieces — which is the whole reason it is still a concept and not a product.',
      palette: ['#E6DCC4', '#B4904E', '#7A6228', '#4A3A12', '#F4EDDD'],
      dir: 'firstlight'
    }
  };

  TT.order = ['goldenhour', 'sundial', 'midnight', 'firstlight'];
  TT.default = 'goldenhour';

  /* ── the three tiers ────────────────────────────────────────────────────
     `approvedPrice` is the DISPLAY price the user approved (399 / 599 / 799).
     It is NOT a transaction price. The chargeable number is always the real
     variant price read back from the platform, and it may only be used when
     it equals `approvedPrice`. When the two disagree the buy control stays
     inert and says so (see Shop.priceText / Shop.buyable in v4-app.js). */
  /* The approved-value registry: the frozen brief's copy and prices, verbatim
     and in one place. `TT.tiers[].approvedPrice` reads from here, so this is
     the single source the shopper-facing price display is built on. It is a
     DISPLAY price only; the chargeable amount always comes from the real
     variant (see Shop.priceText / Shop.buyable in v4-app.js). */
  TT.approved = {
    hero: 'The Heirloom, Re-Engineered.',
    sampleHeadline: 'Buy the wood before you buy the set.',
    club: 'Mahjong is a gathering. Find your table.',
    woodGoldenHour: 'Golden Hour',
    woodSundial: 'Sundial',
    woodMidnight: 'Midnight',
    woodFirstLight: 'First Light',
    tierPaper: 'Paper Box Set',
    tierCollector: 'Collector Box Set',
    tierHeirloom: 'Heirloom Set',
    pricePaper: 399,
    priceCollector: 599,
    priceHeirloom: 799,
    accMat: 75,
    accRacks: 110,
    accShuffler: 26,
    accDice: 5
  };

  TT.tiers = {
    paper: {
      id: 'paper', no: '1', name: 'Paper Box Set',
      approvedPrice: 399,
      sub: 'The game itself, plainly packed.',
      incl: [
        { t: 'Engineered wood-grain tiles', on: true },
        { t: 'Racks & pushers', on: true },
        { t: 'Printed rules card', on: true },
        { t: 'Recyclable paper box', on: true },
        { t: 'Collector box', on: false },
        { t: 'Numbered identity', on: false }
      ]
    },
    collector: {
      id: 'collector', no: '2', name: 'Collector Box Set',
      approvedPrice: 599,
      sub: 'The same tiles, in a box worth keeping.',
      incl: [
        { t: 'Everything in the Paper Box Set', on: true },
        { t: 'Collector box in the matching wood', on: true },
        { t: 'Felt-lined storage for the full set', on: true },
        { t: 'Care cloth', on: true },
        { t: 'Numbered identity', on: false }
      ]
    },
    heirloom: {
      id: 'heirloom', no: '3', name: 'Heirloom Set',
      approvedPrice: 799,
      sub: 'Numbered, grain-matched, and yours alone.',
      hero: true,
      incl: [
        { t: 'Everything in the Collector Box Set', on: true },
        { t: 'A unique number, issued to your set', on: true, hi: true },
        { t: 'Every tile photographed & grain-matched before shipping', on: true, hi: true },
        { t: 'Register your number and lock the set to you', on: true, hi: true },
        { t: 'Matched tile racks', on: true },
        { t: 'Limited release — numbered runs only', on: true }
      ]
    }
  };
  TT.tierOrder = ['paper', 'collector', 'heirloom'];

  /* ── the two secondary axes ──────────────────────────────────────────────
     face  : a REAL physical option (the engraved artwork on the tile). The
             catalog currently exposes NO face option and NO face→variant
             mapping, so two faces can never share one variant. Until a
             mapping exists, a face selection is not orderable and the buy
             control is disabled. `TT.faceMap` is the ingestion interface the
             mapping will be loaded into: { '<wood>|<tier>|<format>|<face>':
             '<variantId>' }. It is empty today on purpose.
     format: the real Edition option (US / Chinese). It selects a different
             variant and a different tile count, but the same tier price. */
  TT.faceMap = {};
  TT.faces = [
    { id: 'traditional', name: 'Traditional', note: 'The faces you already know by heart.' },
    { id: 'recoloured', name: 'Recoloured', note: 'The same faces, pulled into this wood\u2019s palette.' }
  ];
  TT.formats = [
    { id: 'us', name: 'US edition', short: 'US' },
    { id: 'cn', name: 'Chinese edition', short: 'CN' }
  ];

  /* ── gallery media registry ──────────────────────────────────────────────
     The product gallery is DATA, not code. Every entry is keyed by the exact
     selection it belongs to:

         key = '<wood>|<tier>|<face>|<edition>'
         e.g.   'goldenhour|paper|traditional|us'

     and its value is an ORDERED array of slots:

         { role: 'stage' | 'detail' | 'edge' | 'hand' | 'table',
           url:  '<a real Shopify product image url>',
           alt:  '<alt text>' }

     Slot order is the thumbnail order; clicking a thumbnail swaps the main
     frame by slot index. Nothing is pre-filled, so today the page renders the
     4:5 placeholder — honest, and needing no code change the day the media
     exists. The runtime merges window.TT_SHOP.gallery[key] over this file, so
     the same map can also be delivered from an Admin read / theme setting.
     Use REAL product image urls only. Never invent one. */
  TT.gallery = {
    key: function (wood, tier, face, edition) {
      return wood + '|' + tier + '|' + face + '|' + edition;
    },
    slots: {},
    set: function (k, list) { TT.gallery.slots[k] = list || []; },
    for: function (wood, tier, face, edition) {
      return TT.gallery.slots[TT.gallery.key(wood, tier, face, edition)] || [];
    }
  };

  /* ── accessories and singles ────────────────────────────────────────────
     One category list, one id set. A category with price === null is a real
     structural slot whose price has not been set: it renders as a disabled
     "price pending" row and can never enter the cart. */
  TT.parts = {
    mat: {
      id: 'mat', n: '01', name: 'Tablecloth & Mat', group: 'Table',
      price: 75, unit: 'unit pending', unitPending: true, skuPending: true,
      blurb: 'Cut to the full layout, in a tone matched to your wood.',
      layer: null, z: 10,
      styles: [
        { id: 'matched', name: 'Matched to my wood' },
        { id: 'plain', name: 'Plain linen' }
      ]
    },
    racks: {
      id: 'racks', n: '02', name: 'Racks & Pushers', group: 'Play',
      price: 110, unit: 'unit pending', unitPending: true, skuPending: true,
      blurb: 'Same finish, same weight in the hand.',
      layer: null, z: 20,
      styles: [
        { id: 'matched', name: 'Matched to my wood' },
        { id: 'natural', name: 'Natural' }
      ]
    },
    shuffler: {
      id: 'shuffler', n: '03', name: 'Shuffler', group: 'Play',
      price: 26, unit: 'unit pending', unitPending: true, skuPending: true,
      blurb: 'Built for a full set. Sits under the mat, comes off in one lift.',
      layer: null, z: 30,
      styles: [
        { id: 'standard', name: 'Standard' }
      ]
    },
    dice: {
      id: 'dice', n: '04', name: 'Dice', group: 'Play',
      price: 5, unit: 'unit pending', unitPending: true, skuPending: true,
      blurb: 'Weighted to land flat, in the same wood tone as your set.',
      layer: null, z: 40,
      styles: [
        { id: 'matched', name: 'Matched to my wood' },
        { id: 'ivory', name: 'Ivory' }
      ]
    },
    bag: {
      id: 'bag', n: '05', name: 'Cases & Storage', group: 'Carry',
      price: null, unit: 'price pending', unitPending: true, skuPending: true,
      blurb: 'A carry case for the full set. Price not set yet.',
      layer: null, z: 50,
      styles: []
    },
    care: {
      id: 'care', n: '06', name: 'Care Kit', group: 'Care',
      price: null, unit: 'price pending', unitPending: true, skuPending: true,
      blurb: 'Cloth and care notes. Price not set yet.',
      layer: null, z: 60,
      styles: []
    }
  };
  /* the matcher's fixed order — the tiles layer is left of it and is never
     selectable, it is the reference object. */
  TT.partOrder = ['mat', 'racks', 'shuffler', 'dice', 'bag', 'care'];
  TT.matcherOrder = ['mat', 'racks', 'shuffler', 'dice', 'bag', 'care'];

  /* ── tile-face designs, keyed by wood (ONE shape, no array/object clash) ─ */
  TT.designs = {
    goldenhour: [
      { id: 'traditional', n: '01', name: 'Traditional', status: 'live',
        blurb: 'The faces you already know by heart. Crisp, high-contrast, deeply engraved, and instantly readable from any seat at the table.',
        tag: 'Standard on every Golden Hour set' },
      { id: 'recoloured', n: '02', name: 'Traditional, Recoloured', status: 'live',
        blurb: 'The same familiar faces, pulled into the palette of the Golden Hour grain, so the entire set reads as one continuous object.',
        tag: 'Paired specifically to Teak' }
    ],
    sundial: [
      { id: 'traditional', n: '01', name: 'Traditional', status: 'open',
        blurb: 'The standard faces, tuned for the lighter Sundial tonal range.',
        tag: 'In review' },
      { id: 'recoloured', n: '02', name: 'Traditional, Recoloured', status: 'open',
        blurb: 'Faces pulled into the sun-dried bamboo palette.',
        tag: 'In review' }
    ],
    midnight: [
      { id: 'traditional', n: '01', name: 'Traditional', status: 'open',
        blurb: 'High-contrast faces for a near-black tile — the whole problem with ebony is legibility, and this is our answer.',
        tag: 'In review' },
      { id: 'recoloured', n: '02', name: 'Traditional, Recoloured', status: 'open',
        blurb: 'Faces lifted a full step in value so they read against the dark grain.',
        tag: 'In review' }
    ],
    firstlight: [
      { id: 'traditional', n: '01', name: 'Traditional', status: 'open',
        blurb: 'Bone face over bamboo body — the classic two-tone arrangement, done in a single engineered tile.',
        tag: 'In review' },
      { id: 'recoloured', n: '02', name: 'Traditional, Recoloured', status: 'open',
        blurb: 'The same two-tone logic, colour-matched to the dawn palette.',
        tag: 'In review' }
    ]
  };

  /* ── By set: the three tier pages share one structure ─────────────────── */
  TT.bySet = {
    paper: {
      tier: 'paper', name: 'Paper Box Set', eyebrow: 'By set · 01',
      kicker: 'The game itself, plainly packed.',
      h1: 'Everything you need to play tonight.',
      note: 'Scene photograph — a table mid-game, hand reaching for a tile.'
    },
    collector: {
      tier: 'collector', name: 'Collector Box Set', eyebrow: 'By set · 02',
      kicker: 'The same tiles, in a box worth keeping.',
      h1: 'The set that lives on the shelf.',
      note: 'Scene photograph — the box open on a living-room table, evening light.'
    },
    heirloom: {
      tier: 'heirloom', name: 'Heirloom Set', eyebrow: 'By set · 03',
      kicker: 'Numbered, grain-matched, and yours alone.',
      h1: 'One number. One grain. One owner at a time.',
      note: 'Scene photograph — the numbered plaque, close, raking light.'
    }
  };

  /* ── homepage copy blocks ─────────────────────────────────────────────── */
  TT.copy = {
    announceLead: 'Four collections, one game',
    announceItems: [
      'Samples open on every released wood',
      'Heirloom sets ship in numbered, limited runs'
    ],
    heroKicker: 'Golden Hour · No. 01',
    heroH1a: 'The Heirloom,',
    heroH1b: 'Re-Engineered.',
    heroSub: ['Like Wood.', 'Unlike Wood.', 'Beyond Wood.'],
    heroProof: [
      'Heavy. Solid. Satisfying.',
      'The warmth of wood. None of the cracking.',
      'Touch the patina. Time, made yours.'
    ]
  };

  TT.sampleCard = {
    kicker: 'Try the material first',
    h3: 'Buy the wood before you buy the set.',
    lede: 'One box, every released wood, plus a tile face in each. Hold them in daylight and decide with your hands — not with a photo on a screen.',
    cta: 'Order a sample box',
    /* "Ships in 3-5 days, from US stock" was removed: unverified. */
    points: [
      'Tactile — the actual substrate, not a swatch card',
      'Hand-sized: keep it on your desk and turn it over',
      'It takes a patina. Yours, from your own handling.'
    ]
  };

  TT.clubHome = {
    kicker: 'Clubs & Events',
    h2: 'Mahjong is a gathering.',
    h2b: 'Find your table.',
    lede: 'Clubs near you, and what is happening this month. No rules, no reading — just where to sit down.',
    /* "148+ clubs on the map" was removed: unverified. */
    stats: [
      { k: 'Clubs on the map', v: '—', src: 'Directory' },
      { k: 'Within 25 miles', v: '—', src: 'Needs your location' },
      { k: 'Events this month', v: '—', src: 'Calendar feed pending' }
    ],
    cta: 'Find a club near me',
    cta2: 'This month\u2019s events'
  };

  /* ── page targets (independent Page entities, see the Page plan) ───────── */
  TT.routes = {
    home: '/',
    /* the Shop root is an OVERVIEW, never a single wood. It points at the
       store's existing shop landing page so the four woods stay peers. */
    /* The Shop root lands on the V4 homepage four-wood overview itself
       (#collections), so the first Shop click is visibly V4 and no single wood
       is favoured. The existing /pages/sets entity and template are untouched. */
    shopRoot: '/#collections',
    sets: '/pages/sets',
    configurator: '/pages/configure',
    accessoryStudio: '/pages/accessory-studio',
    accessories: '/pages/accessories',
    sample: '/pages/sample-swatch',
    shopWood: {
      goldenhour: '/pages/shop-golden-hour',
      sundial: '/pages/shop-sundial',
      midnight: '/pages/shop-midnight',
      firstlight: '/pages/shop-first-light'
    },
    bySet: {
      paper: '/pages/by-set-paper',
      collector: '/pages/by-set-collector',
      heirloom: '/pages/by-set-heirloom'
    }
  };

  /* handle → entity, so four shop pages and three by-set pages share two
     templates without needing a metafield. */
  TT.handles = {
    'shop-golden-hour': { kind: 'wood', value: 'goldenhour' },
    'shop-sundial': { kind: 'wood', value: 'sundial' },
    'shop-midnight': { kind: 'wood', value: 'midnight' },
    'shop-first-light': { kind: 'wood', value: 'firstlight' },
    'by-set-paper': { kind: 'tier', value: 'paper' },
    'by-set-collector': { kind: 'tier', value: 'collector' },
    'by-set-heirloom': { kind: 'tier', value: 'heirloom' }
  };

  /* ── English state strings (single place, no long warnings) ───────────── */
  TT.text = {
    previewOnly: 'Preview — this collection is not open for ordering yet.',
    unavailable: 'Not available to order yet.',
    pricePending: 'Price pending',
    facePreview: 'Artwork preview only. Your order is the set on the right.',
    addToCart: 'Add to cart',
    adding: 'Adding…',
    added: 'Added to cart',
    soldOut: 'Sold out',
    cartError: 'Could not add to cart. Please try again.',
    emptyCart: 'Your cart is empty.',
    nothingSelected: 'Nothing selected yet',
    /* the three reasons a buy control is inert, said plainly */
    notReady: 'Not open for ordering yet.',
    priceMismatch: 'Price not confirmed for this option yet.',
    faceUnmapped: 'This tile-face choice is not orderable yet.',
    unitPending: 'Unit not confirmed yet.',
    /* a selected row that has no price yet KEEPS its place in the buying list;
       the total is then explicitly partial and nothing may be added to cart */
    quoteIncomplete: 'Quote incomplete — a selected item has no price yet.',
    totalPartial: 'partial',
    /* the ACCESSORIES still carry a preliminary figure, so they keep the word;
       the box sets do not — their 399 / 599 / 799 are confirmed */
    provisional: 'Provisional',
    priceDrift: 'Catalog figure differs — the cart confirms the live price.',
    /* a category with no style data says so instead of inventing a choice */
    stylePending: 'Style options not set yet'
  };

  /* ── helpers (hand-parsed so the pages still work over file://) ────────── */
  TT.qparam = function (k) {
    var m = new RegExp('[?&]' + k + '=([^&]+)').exec(window.location.search || '');
    if (m) return decodeURIComponent(m[1]).toLowerCase();
    var h = (window.location.hash || '').replace(/^#/, '');
    return (h.indexOf('=') === -1) ? h.toLowerCase() : '';
  };
  TT.alias = {
    teak: 'goldenhour', bamboo: 'sundial', ebony: 'midnight',
    'bamboo-bone': 'firstlight', bone: 'firstlight',
    'golden-hour': 'goldenhour', 'first-light': 'firstlight'
  };
  TT.resolveWood = function () {
    var w = TT.qparam('wood');
    if (TT.collections[w]) return w;
    if (TT.alias[w] && TT.collections[TT.alias[w]]) return TT.alias[w];
    return TT.default;
  };
  TT.resolveTier = function () {
    var t = TT.qparam('tier');
    return TT.tiers[t] ? t : 'heirloom';
  };
  TT.money = function (n) {
    return (n === null || n === undefined || isNaN(n)) ? '—' : '$' + n;
  };

  /* the page handle is authoritative for the two shared templates */
  TT.pageEntity = function (handle) {
    var h = (handle || '').replace(/^\/?pages\//, '').replace(/\/$/, '');
    if (TT.handles[h]) return TT.handles[h];
    /* a Shop page that is not in the explicit map still resolves from its own
       handle (shop-golden-hour → goldenhour), so a wood page can never render
       empty just because the map was not extended by hand. */
    var slug = h.replace(/^shop-/, '').replace(/-/g, '');
    if (TT.collections[slug]) return { kind: 'wood', value: slug };
    return null;
  };

})(window.TT);
